/**
 * Shared primitive Zod schemas. Used as building blocks throughout domain
 * schemas. Centralized so format constraints (e.g. ULID, E.164, ISO codes)
 * stay consistent.
 */

import { z } from 'zod';

/** UUID v4. */
export const UuidSchema = z.string().uuid();
export type Uuid = z.infer<typeof UuidSchema>;

/** ULID — sortable identifier. Used for messages and append-only events. */
export const UlidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'invalid ULID');
export type Ulid = z.infer<typeof UlidSchema>;

/** ISO-3166 alpha-3 country code (e.g. PAK, USA). Uppercase. */
export const Iso3CountrySchema = z
  .string()
  .regex(/^[A-Z]{3}$/, 'must be a 3-letter ISO-3166 alpha-3 country code (uppercase)');
export type Iso3Country = z.infer<typeof Iso3CountrySchema>;

/** ISO-639 language code (e.g. en, ur, ar). Lowercase. */
export const IsoLanguageSchema = z.string().regex(/^[a-z]{2,3}$/, 'invalid ISO-639 language code');
export type IsoLanguage = z.infer<typeof IsoLanguageSchema>;

/** ISO-4217 currency code (e.g. USD, PKR). Uppercase. */
export const IsoCurrencySchema = z.string().regex(/^[A-Z]{3}$/, 'invalid ISO-4217 currency code');
export type IsoCurrency = z.infer<typeof IsoCurrencySchema>;

/**
 * E.164 phone number. Plus sign + 8-15 digits. Country code included.
 * Validation is structural only; carrier-level validation happens at OTP send.
 */
export const E164PhoneSchema = z
  .string()
  .regex(/^\+[1-9]\d{7,14}$/, 'must be E.164 (e.g. +14155550100)');
export type E164Phone = z.infer<typeof E164PhoneSchema>;

/** Lower-cased, trimmed email. */
export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .max(254, 'email exceeds RFC limit');
export type Email = z.infer<typeof EmailSchema>;

/** S3 object key. Allows path-like characters but no leading/trailing slash. */
export const S3KeySchema = z
  .string()
  .min(1)
  .max(1024)
  .regex(/^[^/].*[^/]$|^[^/]$/, 'S3 key cannot start or end with /');
export type S3Key = z.infer<typeof S3KeySchema>;

/** ISO-8601 datetime string. */
export const IsoDateTimeSchema = z.string().datetime({ offset: true });
export type IsoDateTime = z.infer<typeof IsoDateTimeSchema>;

/** Date in YYYY-MM-DD format. */
export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
export type IsoDate = z.infer<typeof IsoDateSchema>;

/** Latitude (-90..90). */
export const LatitudeSchema = z.number().gte(-90).lte(90);
export type Latitude = z.infer<typeof LatitudeSchema>;

/** Longitude (-180..180). */
export const LongitudeSchema = z.number().gte(-180).lte(180);
export type Longitude = z.infer<typeof LongitudeSchema>;

/** Geographic point. */
export const GeoPointSchema = z.object({
  lat: LatitudeSchema,
  lng: LongitudeSchema,
});
export type GeoPoint = z.infer<typeof GeoPointSchema>;

/** Idempotency key — client-generated ULID for safe retries. */
export const IdempotencyKeySchema = z.string().min(8).max(64);
export type IdempotencyKey = z.infer<typeof IdempotencyKeySchema>;
